import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import clone from 'lodash-es/clone';
import { AccountService } from 'app/core';
import { ServicePagination } from '@shared/models';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { Contact } from '@modules/contacts/contacts.types';
import { PartialFormField } from '@shared/components/form-fields/form-field.types';
import { Address, Project, Property, ProjectPagination, Image } from '@modules/projects/projects.types';
import { ReportPeriod } from '../asset-management/models';
import { TimeSeriesChartDataResponse } from './models/time-series-chart';
import { RelatedToType } from '@shared/enums';
import { FieldDef } from '@core/models';
import { Cacheable } from 'ts-cacheable';
import { TsSnackbarService } from 'tsui';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';

const propertyCacheBuster$ = new Subject<void>();

@Injectable({
  providedIn: 'root',
})
export class PropertiesService {
  private _property: BehaviorSubject<Property | null>;
  private _properties: BehaviorSubject<Property[] | null>;
  private _pagination: BehaviorSubject<ServicePagination | null>;
  private _accountId: string;
  private _panelDefinition: BehaviorSubject<any | null>;
  private _changePanelState: BehaviorSubject<boolean | false>;
  private _contacts: BehaviorSubject<Contact[] | null>;

  constructor(
    private _httpClient: HttpClient,
    private _accountService: AccountService,
    private _snackBar: TsSnackbarService,
  ) {
    this._property = new BehaviorSubject(null);
    this._properties = new BehaviorSubject(null);
    this._pagination = new BehaviorSubject(null);
    this._changePanelState = new BehaviorSubject(false);
    this._panelDefinition = new BehaviorSubject(null);
    this._contacts = new BehaviorSubject(null);

    this._accountService.currentAccount.subscribe((accountData) => {
      this._accountId = accountData.id;
    });
  }

  get property$(): Observable<Property> {
    return this._property.asObservable();
  }

  get properties$(): Observable<Property[]> {
    return this._properties.asObservable();
  }

  get pagination$(): Observable<ServicePagination> {
    return this._pagination.asObservable();
  }

  get changePanelState$(): Observable<boolean> {
    return this._changePanelState.asObservable();
  }

  get contacts$(): Observable<Contact[]> {
    return this._contacts.asObservable();
  }

  get panelDefinition$(): Observable<any> {
    return this._panelDefinition.asObservable();
  }

  get currentProperty(): Property {
    return this._property.value;
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  private _massageRequestFields(fields: string[]) {
    let requestFields = {};
    for (let field of fields) {
      requestFields[field] = true;
    }
    if (!fields.includes('id')) {
      requestFields['id'] = true;
    }
    if (!fields.includes('address_string')) {
      requestFields['address_string'] = true;
    }
    if (!fields.includes('featured_image_url')) {
      requestFields['featured_image_url'] = true;
    }
    if (!fields.includes('name')) {
      requestFields['name'] = true;
    }
    if (!fields.includes('address_string')) {
      requestFields['address_string'] = true;
    }

    if (fields.includes('address')) {
      requestFields['address'] = {
        latitude: true,
        longitude: true,
      };
    }
    return requestFields;
  }

  getPropertiesWithoutSort(
    fields: string[] = [],
    queryFilters: any = {},
    page: number = 0,
    size: number = 20,
    search: string = '',
    format: string = '',
    group: string = '',
    updateNext = true,
    savedViewId: number = null,
  ): Observable<any> {
    let node = this._massageRequestFields(clone(fields));

    const filter = clone(queryFilters);
    for (const key in filter) {
      if (filter[key] && filter[key].length > 0) {
        if (Array.isArray(filter[key])) {
          filter[key] = filter[key].join(',');
        }
      } else if (filter[key] && filter[key].length === 0) {
        if (Array.isArray(filter[key])) {
          filter[key] = '';
        }
      }
    }
    filter['q'] = search;

    const query = {
      query: {
        properties: {
          __args: {
            filter,
            page: page + 1,
            perPage: size,
          },
          pageInfo: {
            hasNextPage: true,
            hasPreviousPage: true,
            endCursor: true,
            startCursor: true,
            totalCount: true,
            filteredCount: true,
          },
          edges: {
            cursor: true,
            node,
          },
        },
      },
    };

    const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

    let url = `graphql`;

    if (format) {
      url += `?format=${format}`;
    }

    if (group) {
      url += `&group=${group}`;
    }

    if (savedViewId) {
      url += `&saved_view_id=${savedViewId}`;
    }

    return this._httpClient
      .post<{ pagination: ServicePagination; properties: Property[] }>(url, {
        query: gqlQuery,
      })
      .pipe(
        map((response: any) => {
          if (format == 'csv' || format == 'pdf') {
            return response.data;
          } else {
            const pageInfo = response.data.properties.pageInfo;

            const lastPage = Math.max(Math.ceil(pageInfo.filteredCount / size), 1);
            const begin = page * size;
            const end = Math.min(size * (page + 1), pageInfo.filteredCount);

            const pagination = {
              length: pageInfo.filteredCount,
              size: size,
              page: page,
              lastPage: lastPage,
              startIndex: begin,
              endIndex: end,
            };
            const properties = response.data.properties.edges.map((edge) => edge.node);
            if (updateNext) {
              this._pagination.next(pagination);
              this._properties.next(properties);
            }
            return {
              pagination,
              properties,
            };
          }
        }),
      );
  }

  getProperties(
    fields: string[] = [],
    queryFilters: any = {},
    page: number = 0,
    size: number = 20,
    sort = 'name',
    order: 'asc' | 'desc' | '' = 'asc',
    search: string = '',
    format: string = '',
    group: string = '',
    enableNestedFilters = false,
    fieldsToInclude: string[] = [],
  ): Observable<{ pagination: ServicePagination; properties: Property[] }> {
    const collectionKey = !enableNestedFilters ? 'properties' : 'propertiesSqlBased';
    const fallbackQ = enableNestedFilters ? '' : '*';
    let node = this._massageRequestFields(clone(fields));

    const filter = clone(queryFilters);
    for (const key in filter) {
      if (filter[key] && filter[key].length > 0) {
        if (Array.isArray(filter[key])) {
          filter[key] = filter[key].join(',');
        }
      } else if (filter[key] && filter[key].length === 0) {
        if (Array.isArray(filter[key])) {
          filter[key] = '';
        }
      }
    }

    filter['q'] = search || queryFilters['q'] || fallbackQ;

    if (fieldsToInclude.length) {
      filter['fields'] = fieldsToInclude.join(',');
    }

    const query = {
      query: {
        [collectionKey]: {
          __args: {
            filter,
            page: page + 1,
            perPage: size,
            sort: { field: sort, order: order },
          },
          pageInfo: {
            hasNextPage: true,
            hasPreviousPage: true,
            endCursor: true,
            startCursor: true,
            totalCount: true,
            filteredCount: true,
          },
          edges: {
            cursor: true,
            node,
          },
        },
      },
    };
    const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

    return this._httpClient
      .post<{ pagination: ProjectPagination; projects: Project[] }>(`graphql?format=${format}&group=${group}`, {
        query: gqlQuery,
      })
      .pipe(
        map((response: any) => {
          if (format == 'csv' || format == 'pdf') {
            return response.data;
          } else {
            const pageInfo = response.data[collectionKey].pageInfo;

            const lastPage = Math.max(Math.ceil(pageInfo.filteredCount / size), 1);
            const begin = page * size;
            const end = Math.min(size * (page + 1), pageInfo.filteredCount);

            const pagination = {
              length: pageInfo.filteredCount,
              size: size,
              page: page,
              lastPage: lastPage,
              startIndex: begin,
              endIndex: end,
            };
            const properties = response.data[collectionKey].edges.map((edge) => edge.node);
            this._pagination.next(pagination);
            this._properties.next(properties);
            return {
              pagination,
              properties,
            };
          }
        }),
      );
  }

  @Cacheable({
    cacheBusterObserver: propertyCacheBuster$,
    maxAge: 10000,
    slidingExpiration: true,
  })
  getPropertyById(id: string, touch: boolean = false): Observable<Property> {
    return this._httpClient
      .get<{
        property: Property;
      }>(`properties/${id}?touch=${touch}`)
      .pipe(
        map(({ property }) => {
          this._property.next(property);
          return property;
        }),
      );
  }

  updatePropertyAddress(address: Address, propertyId?: number): Observable<Address> {
    return this.property$.pipe(
      take(1),
      switchMap((project) =>
        this._httpClient
          .put<any>(`addresses/${address.id}?property_id=${propertyId}`, {
            address: address,
          })
          .pipe(
            map((response) => {
              return response.address;
            }),
          ),
      ),
    );
  }

  saveProperty(
    property: Property | PartialFormField,
    fillProperty: Property | PartialFormField = {},
    updateGeoCode: boolean = false,
  ): Observable<Property> {
    if (property.id) {
      const params: any = {
        property,
      };
      return this.properties$.pipe(
        take(1),
        switchMap((properties) =>
          this._httpClient.put<any>(`properties/${property.id}${updateGeoCode ? '?regeocode=true' : ''}`, params).pipe(
            map((response) => {
              let updatedProperty = response.property;
              updatedProperty = { ...fillProperty, ...property, ...response.property };
              // Find the index of the updated contact
              if (properties) {
                const index = properties.findIndex((item) => item.id === property.id);

                // Update the contact
                properties[index] = updatedProperty;

                // Update the contacts
                this._properties.next(properties);
              }

              // Return the updated contact
              return updatedProperty;
            }),
            switchMap((updatedProperty) =>
              this.property$.pipe(
                take(1),
                tap((item) => {
                  if (item && +item.id === +property.id) {
                    // Update the contact if it's selected
                    this._property.next(updatedProperty);

                    // Return the updated contact
                    return updatedProperty;
                  }
                }),
              ),
            ),
          ),
        ),
        TsRxjsOperators.successAndErrorSnackbar(
          this._snackBar,
          'Property saved successfully',
          'Failed to save property',
        ),
      );
    } else {
      return this.properties$.pipe(
        take(1),
        switchMap((properties) =>
          this._httpClient.post<any>(`properties`, { property: property }).pipe(
            map((reponse) => {
              if (properties) {
                // Update the contacts with the new contact
                this._properties.next([reponse.property, ...properties]);
              }

              //update so that we can add properties
              this._property.next(reponse.property);

              // Return the new contact
              return reponse.property;
            }),
          ),
        ),
        TsRxjsOperators.successAndErrorSnackbar(
          this._snackBar,
          'Property saved successfully',
          'Failed to save property',
        ),
      );
    }
  }

  /**
   * Use this method if if you don't have the properties observable and just want to update the property
   * @param property
   * @returns
   */
  savePropertyDirect(
    property: Property | PartialFormField,
    fillProperty: Property | PartialFormField = {},
  ): Observable<{ property: Property }> {
    if (property.id) {
      const params: any = {
        property,
      };
      return this._httpClient.put<any>(`properties/${property.id}`, params).pipe(
        map((response) => {
          let updatedProperty = response.property;
          updatedProperty = { ...fillProperty, ...property, ...response.property };
          return { property: updatedProperty };
        }),
      );
    } else {
      return this._httpClient.post<any>(`properties`, { property: property }).pipe(
        map((reponse) => {
          return { property: reponse.property };
        }),
      );
    }
  }

  deleteProperty(id: number): Observable<any> {
    return this.properties$.pipe(
      take(1),
      switchMap((properties) =>
        this._httpClient.delete<any>(`properties/${id}`).pipe(
          map((response) => {
            if (properties) {
              // Find the index of the deleted label within the labels
              const index = properties.findIndex((item) => item.id == id);

              // Delete the label
              properties.splice(index, 1);

              // Update the labels
              this._properties.next(properties);
            }

            // Return the deleted status
            return response.property;
          }),
        ),
      ),
    );
  }

  /**
   * Upload Image to property
   */
  uploadImage(file: File): Observable<Image> {
    const uploadData = new FormData();
    uploadData.append('image[attachment]', file);

    return this.property$.pipe(
      take(1),
      switchMap((property) =>
        this._httpClient.post<any>(`images?property_id=${property.id}`, uploadData).pipe(
          map((response) => {
            let images = property.images;
            images.push(response?.image);
            property.images = images;
            this._property.next(property);
            return response;
          }),
        ),
      ),
    );
  }

  /**
   * Delete image
   */
  deleteImage(id: number): Observable<any> {
    return this.property$.pipe(
      take(1),
      switchMap((property) =>
        this._httpClient.delete<any>(`images/${id}`).pipe(
          map((response) => {
            let images = property.images;
            // Find the index of the deleted label within the labels
            const index = images.findIndex((item) => item.id === id);

            // Delete the label
            images.splice(index, 1);
            property.images = images;

            // Update the labels
            this._property.next(property);

            // Return the deleted status
            return response;
          }),
        ),
      ),
    );
  }

  getContacts(): Observable<Contact[]> {
    return this.property$.pipe(
      take(1),
      switchMap((property) => this.getContactassociations(property)),
    );
  }

  getContactassociations(property) {
    return this._httpClient.get<any>(`contact_associations?related_to_type=Property&related_to_id=${property.id}`).pipe(
      map((response) => {
        let contacts = [];

        if (response?.contact_associations) {
          contacts = response.contact_associations.filter((ca) => ca.contact != null).map((ca) => ca.contact);
        }

        this._contacts.next(contacts);

        return contacts;
      }),
    );
  }

  updateContactInfo(contact: Contact): Observable<Contact> {
    return this.contacts$.pipe(
      take(1),
      switchMap((contacts) =>
        this._httpClient
          .patch<any>(`contacts/${contact.id}`, {
            contact,
          })
          .pipe(
            map((response) => {
              if (contacts) {
                // Find the index of the updated contact
                const index = contacts.findIndex((item) => item.id === contact.id);

                // Update the contact
                contacts[index] = response.contact;

                // Update the contacts
                this._contacts.next(contacts);
              }
              return contact;
            }),
          ),
      ),
    );
  }

  createContactAssociation(contact: Contact, propertyId: number): Observable<Contact> {
    const contactAssociation = {
      related_to_id: propertyId,
      related_to_type: 'Property',
      contact: contact,
    };
    return this.contacts$.pipe(
      take(1),
      switchMap((contacts) =>
        this._httpClient
          .post<any>(`contact_associations`, {
            contact_association: contactAssociation,
          })
          .pipe(
            map((response) => {
              let newContact = response.contact_association.contact;
              if (contacts) {
                contacts.unshift(newContact);
                this._contacts.next(contacts);
              }
              return newContact;
            }),
          ),
      ),
    );
  }

  deleteContactAssociation(id: number, propertyId: number): Observable<any> {
    return this.contacts$.pipe(
      take(1),
      switchMap((contacts) =>
        this._httpClient
          .delete<any>(`contact_associations/${id}?related_to_type=Property&related_to_id=${propertyId}`)
          .pipe(
            map((response) => {
              // Find the index of the deleted label within the labels
              const index = contacts.findIndex((item) => item.id == id.toString());
              // Delete the label
              contacts.splice(index, 1);
              // Update the labels
              this._contacts.next(contacts);

              // Return the deleted status
              return response;
            }),
          ),
      ),
    );
  }

  generatePropertyDocument(
    id: number,
    format: string = 'json',
    file_resource_id: number = null,
    sendToDocusign: boolean = null,
  ): Observable<any> {
    return this._httpClient
      .get<any>(
        // eslint-disable-next-line max-len
        `properties/${id}?format=${format}&file_resource_id=${file_resource_id}&send_to_docusign=${sendToDocusign}`,
      )
      .pipe(
        map((response) => {
          if (format == 'pdf' || format == 'docx') {
            return response;
          } else {
            this._property.next(response.property);
            return response.project;
          }
        }),
      );
  }

  getPropertyTimeSeriesChartData(
    { start_date, end_date }: ReportPeriod,
    fieldNames: string[],
    objectId: number,
    objectType: RelatedToType,
  ): Observable<TimeSeriesChartDataResponse> {
    const params = new HttpParams()
      .set('start_date', start_date)
      .set('end_date', end_date)
      .set('related_to_id', objectId.toString())
      .set('related_to_type', objectType)
      .set('field_names', fieldNames.join(','))
      .set('mode', 'asset_management');

    return this._httpClient.get<TimeSeriesChartDataResponse>('time_series', { params });
  }

  getPropertyTimeSeriesFieldOptions(): Observable<FieldDef[]> {
    return this._httpClient
      .get<{ time_series_custom_fields: FieldDef[] }>('time_series_charts/fields_for?related_to_type=Property')
      .pipe(map((response) => response.time_series_custom_fields));
  }

  createContact(contact: Contact): Observable<Contact> {
    return this._httpClient.post<any>(`contacts`, { contact: contact }).pipe(
      map((response) => {
        return response.contact;
      }),
    );
  }

  changePanelLayout(state) {
    this._changePanelState.next(state);
    return state;
  }

  addPanelDefinition(panelDef) {
    this._panelDefinition.next(panelDef);
    return panelDef;
  }
}
